import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { FileUploadUrlRequest } from '../request-models/file-upload-url-request.model';
import { CertificationUploadUrl } from '../response-models/certification-upload-url.model';
import { LogoUploadUrlResponse } from '../response-models/logo-upload-url-response.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * Admin file uploads: the pre-sign routes under `/admin/file-uploads` (hub `admin-file-uploads-controller.ts`),
 * plus the S3 PUT and the file checks shared by every upload flow. The flow is:
 * 1. Ask the hub for a pre-signed URL (here, or on the owning service for nested routes such as drone resources)
 * 2. `uploadToSignedUrl()`: PUT the bytes straight to S3
 * 3. Save the returned key / URL through the entity's own service
 *
 * Hub failures reject with an `ApiError`. Ported from the web portal's `FileUploadService`.
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadService extends ApiService {
  constructor() {
    super('admin/file-uploads');
  }

  /** POST /admin/file-uploads/chemical-logo. Signed public-read with a Content-Type: PUT it with `{ publicRead: true, contentType }`. */
  getChemicalLogoUploadUrl(filename: string): Promise<LogoUploadUrlResponse> {
    return this.requestUploadUrl('chemical-logo', { filename }, toLogoUploadUrl);
  }

  /** POST /admin/file-uploads/sponsor-logo. Signed public-read with a Content-Type: PUT it with `{ publicRead: true, contentType }`. */
  getSponsorLogoUploadUrl(filename: string): Promise<LogoUploadUrlResponse> {
    return this.requestUploadUrl('sponsor-logo', { filename }, toLogoUploadUrl);
  }

  /** POST /admin/file-uploads/certification. Signed private: PUT it without `publicRead`. */
  getCertificationUploadUrl(filename: string): Promise<CertificationUploadUrl> {
    return this.requestUploadUrl('certification', { filename }, toCertificationUploadUrl);
  }

  /**
   * PUT a file to a pre-signed S3 URL. Uses `fetch`, not HttpClient, so the auth interceptor never sends the
   * Firebase token to S3. Pass `publicRead` only when the hub signed the URL with the `public-read` ACL
   * (chemical and sponsor logos), and `contentType` when it signed one (logos): the headers must then match the
   * signature.
   */
  async uploadToSignedUrl(url: string, file: File, options: { publicRead?: boolean; contentType?: string } = {}): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': options.contentType ?? (file.type || 'application/octet-stream') };
    if (options.publicRead) {
      headers['x-amz-acl'] = 'public-read';
    }
    const response = await fetch(url, { method: 'PUT', body: file, headers });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Check if a file is an allowed type.
   *
   * Keep this list in sync with the `accept` attribute on any file input that
   * feeds this service — `accept` only filters the OS picker and is bypassed
   * entirely by drag-and-drop, so this is the real gate.
   */
  isAllowedFileType(file: File): boolean {
    const allowedTypes = [
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      // PDFs
      'application/pdf',
      // Documents
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
    ];

    // Must match VALID_DRONE_RESOURCE_EXTENSIONS on the Hub and the `accept`
    // attribute on any input feeding this service.
    const allowedExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'webp',
      'pdf',
      'doc', 'docx', 'xls', 'xlsx', 'txt',
    ];

    const extension = this.getFileExtension(file.name);

    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
  }

  /**
   * Get max file size in bytes (100MB)
   */
  getMaxFileSize(): number {
    return 100 * 1024 * 1024; // 100MB
  }

  /**
   * Validate a file against the type and size rules. Returns null when the
   * file is acceptable, or a user-facing message explaining why it isn't.
   */
  validate(file: File): string | null {
    if (!this.isAllowedFileType(file)) {
      return 'That file type isn\'t supported.';
    }
    if (file.size > this.getMaxFileSize()) {
      return `File is too large (${this.formatFileSize(file.size)}). Max is ${this.formatFileSize(this.getMaxFileSize())}.`;
    }
    return null;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  /** POSTs a pre-sign request; rejects when the hub answers without an upload URL. */
  private async requestUploadUrl<R>(path: string, body: FileUploadUrlRequest, read: (data: unknown) => R | null): Promise<R> {
    const result = await firstValueFrom(this.request('POST', { path }, read, body));
    if (!result) {
      throw new Error('Could not prepare the upload.');
    }
    return result;
  }
}

function toLogoUploadUrl(data: unknown): LogoUploadUrlResponse | null {
  const source = isRecord(data) ? data : {};
  const uploadUrl = nullableString(source['uploadUrl']);
  const fileUrl = nullableString(source['fileUrl']);
  const contentType = nullableString(source['contentType']) ?? undefined;
  if (!uploadUrl || !fileUrl) {
    return null;
  }
  return contentType ? { uploadUrl, fileUrl, contentType } : { uploadUrl, fileUrl };
}

function toCertificationUploadUrl(data: unknown): CertificationUploadUrl | null {
  const logo = toLogoUploadUrl(data);
  return logo ? { uploadUrl: logo.uploadUrl, fileKey: logo.fileUrl } : null;
}
